import { FC } from 'react';
import { PackageFilterModel } from 'ts/package/classes/PackageFilterModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { formatPriceCurrency } from 'utils/utils';
import { onClickClose } from 'utils/responsive';
import PackageResultFilterSlider from './PackageFilterSlider';
import Rating from 'components/ui/Rating/Rating';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { getBoardName } from 'utils/package';
import { isTT, isNetravel } from 'constants/generic';

interface PackageFilterProps {
  filter: PackageFilterModel;
  onChangeFilter: (filter: PackageFilterModel) => void;
  onClickReset: () => void;
  isTour?: boolean;
}

const PackageFilter: FC<PackageFilterProps> = ({
  filter,
  onChangeFilter,
  onClickReset,
  ...props
}) => {
  const colorContrast3 = getComputedStyle(document.body).getPropertyValue('--color-contrast-3');
  const colorDark = getComputedStyle(document.body).getPropertyValue('--color-dark');
  const [t] = useTranslation();

  const showClearFilter = () => {
    return (
      filter.name.value ||
      filter.provider.value ||
      filter.stars.some((star) => star.checked) ||
      filter.price.value[0] !== filter.price.min ||
      filter.price.value[1] !== filter.price.max ||
      filter.destination.some((des) => des.checked) ||
      (filter.destinationReturn ?? []).some((airport) => airport.checked) ||
      filter.features.some((feature) => feature.checked) ||
      filter.boards.some((board) => board.checked) ||
      filter.fastOK
    );
  };

  return (
    <div className="flight-filter-wr default-shadow mb-30">
      <div className="flight-filter">
        <div className="results-responsive-header results-responsive-header-flight">
          <span className="title text-md">Filter</span>
          <FontAwesomeIcon icon={faTimes} onClick={onClickClose} />
        </div>
        <section className="flight-filter-section">
          <div className="flex-between mb-15">
            <div className="text-bold">{t('hotel.name')}</div>
            {showClearFilter() && (
              <div className="text-underline text-sm" onClick={onClickReset}>
                {t('filter.reset')}
              </div>
            )}
          </div>
          <SelectCustom
            onlyLtr={true}
            options={filter.name.options}
            value={filter.name.value}
            onChange={(value) =>
              onChangeFilter({ ...filter, name: { ...filter.name, value: value } })
            }
          />
        </section>
        {!isTT && !isNetravel && (
          <section className="flight-filter-section">
            <div className="flex-between mb-15">
              <div className="text-bold">{t('package.provider')}</div>
            </div>
            <SelectCustom
              options={filter.provider.options}
              value={filter.provider.value}
              onChange={(value) =>
                onChangeFilter({ ...filter, provider: { ...filter.provider, value: value } })
              }
              onlyLtr={true}
            />
          </section>
        )}
        <section className="flight-filter-section">
          <div className="flex-between mb-15">
            <div className="text-bold">{t('package.immediate_approval')}</div>
          </div>
          <Checkbox
            label={t('package.immediate_approval')}
            checked={filter.fastOK}
            onChange={(val) => onChangeFilter({ ...filter, fastOK: val })}
          />
        </section>
        {!props.isTour && (
          <section className="flight-filter-section">
            <div className="text-bold mb-15">{t('search.star_rating')}</div>
            {filter.stars.map((star, index) => (
              <Checkbox
                key={index}
                disabled={star.disabled}
                checked={star.checked}
                onChange={(value) => {
                  const newStars = [...filter.stars];
                  newStars[index].checked = value;
                  onChangeFilter({ ...filter, stars: newStars });
                }}
                htmlLabel={
                  <div className="flex-between mb-8">
                    <Rating
                      value={star.count.toString()}
                      color={star.disabled ? colorDark : colorContrast3}
                    />
                    {!star.disabled && (
                      <span className="text-xsm">
                        {formatPriceCurrency(star.minPrice, filter.currencySign)}
                      </span>
                    )}
                  </div>
                }
              />
            ))}
          </section>
        )}

        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('package.nts')}</div>{' '}
          {filter.nights.map((night, index) => (
            <Checkbox
              key={index}
              className="mb-5 text-xsm"
              checked={night.checked}
              onlyChecked={true}
              onChange={() => {
                const newNights = filter.nights.map((item) => ({ ...item, checked: false }));
                newNights[index].checked = true;
                onChangeFilter({ ...filter, nights: newNights });
              }}
              htmlLabel={
                <div className="flex-between">
                  <span>{`${night.count} ${t('package.nts')}`}</span>
                  {formatPriceCurrency(night.minPrice, filter.currencySign)}
                </div>
              }
            />
          ))}
        </section>
        {filter.price.min < filter.price.max && (
          <section className="flight-filter-section">
            <div className="text-bold mb-15">{t('price')}</div>
            <PackageResultFilterSlider
              filter={filter}
              onChangeFilter={onChangeFilter}
              maxStr={formatPriceCurrency(filter.price.max, filter.currencySign)}
              minStr={formatPriceCurrency(filter.price.min, filter.currencySign)}
              type="price"
            />
          </section>
        )}

        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('filter.airports')}</div>
          {(filter.destinationReturn ?? []).map((airport, index) => (
            <Checkbox
              className="mb-5 text-xsm"
              onChange={(value) => {
                const newDestinationReturn = [...(filter.destinationReturn ?? [])];
                newDestinationReturn[index].checked = value;
                onChangeFilter({ ...filter, destinationReturn: newDestinationReturn });
              }}
              htmlLabel={
                <div className="flex-between">
                  <span>{airport.name}</span>
                  {formatPriceCurrency(airport.price, filter.currencySign)}
                </div>
              }
            />
          ))}
        </section>

        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('package.destination')}</div>
          {filter.destination.map((des, index) => (
            <Checkbox
              key={index}
              className="mb-5 text-xsm"
              onChange={(value) => {
                const newDestination = [...filter.destination];
                newDestination[index].checked = value;
                onChangeFilter({ ...filter, destination: newDestination });
              }}
              htmlLabel={
                <div className="flex-between">
                  <span>{des.name}</span>
                  {formatPriceCurrency(des.price, filter.currencySign)}
                </div>
              }
            />
          ))}
        </section>

        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('package.board_type')}</div>
          {filter.boards.map((board, index) => (
            <Checkbox
              key={index}
              className="mb-5 text-xsm"
              onChange={(value) => {
                const newBoards = [...filter.boards];
                newBoards[index].checked = value;
                onChangeFilter({ ...filter, boards: newBoards });
              }}
              htmlLabel={
                <div className="flex-between">
                  <span>{getBoardName(board.name)}</span>
                  {formatPriceCurrency(board.price, filter.currencySign)}
                </div>
              }
            />
          ))}
        </section>
        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('package.deal_features')}</div>
          {filter.features.map((feature, index) => (
            <Checkbox
              key={index}
              className="mb-5 text-xsm"
              onChange={(value) => {
                const newFeatures = [...filter.features];
                newFeatures[index].checked = value;
                onChangeFilter({ ...filter, features: newFeatures });
              }}
              htmlLabel={
                <div className="flex-between text-cap">
                  <span>{t(feature.displayName)}</span>
                  {formatPriceCurrency(feature.price, filter.currencySign)}
                </div>
              }
            />
          ))}
        </section>
      </div>
    </div>
  );
};

export default withErrorBoundary(PackageFilter, 'PackageFilter');
